type FormProps = {
  $hasDestinationInput: boolean;
  $hasMoreOptionsToggle: boolean;
  $hasLocationError: boolean;
  $hasDateError: boolean;
};

const joinAreas = (areas: string[]) => areas.filter(Boolean).join('\n');

export const getDefaultGridTemplateAreas = ({
  $hasDestinationInput,
  $hasMoreOptionsToggle,
  $hasLocationError,
  $hasDateError,
}: FormProps) =>
  joinAreas([
    "'departure'",
    $hasDestinationInput ? "'destination'" : '',
    $hasLocationError ? "'location-error'" : '',
    "'one-way-toggle'",
    "'date-picker'",
    "'time-picker'",
    $hasDateError ? "'date-error'" : '',
    "'age'",
    "'button'",
    $hasMoreOptionsToggle ? "'options-toggle'" : '',
  ]);

export const getDefaultWideGridTemplateAreas = ({
  $hasDestinationInput,
  $hasMoreOptionsToggle,
  $hasLocationError,
  $hasDateError,
}: FormProps) =>
  joinAreas([
    `'departure ${$hasDestinationInput ? 'destination' : 'departure'}'`,
    $hasLocationError ? "'location-error location-error'" : '',
    "'one-way-toggle one-way-toggle'",
    "'date-picker date-picker'",
    "'time-picker time-picker'",
    $hasDateError ? "'date-error date-error'" : '',
    "'age age'",
    "'button button'",
    $hasMoreOptionsToggle ? "'options-toggle options-toggle'" : '',
  ]);

export const getMediumGridTemplateAreas = ({
  $hasDestinationInput,
  $hasMoreOptionsToggle,
  $hasLocationError,
  $hasDateError,
}: FormProps) =>
  joinAreas([
    `'departure ${$hasDestinationInput ? 'destination' : 'departure'}'`,
    $hasLocationError ? "'location-error location-error'" : '',
    "'one-way-toggle one-way-toggle'",
    "'date-picker time-picker'",
    $hasDateError ? "'date-error date-error'" : '',
    "'age button'",
    $hasMoreOptionsToggle ? "'options-toggle options-toggle'" : '',
  ]);

/**
 * The card layout of the index page variant on tablets: two equal columns
 * with location | rental period, times | button, toggle | driver age.
 */
export const getCardMediumGridTemplateAreas = ({
  $hasDestinationInput,
  $hasMoreOptionsToggle,
  $hasLocationError,
  $hasDateError,
}: FormProps) =>
  joinAreas(
    $hasDestinationInput
      ? [
          "'departure destination'",
          $hasLocationError ? "'location-error location-error'" : '',
          "'date-picker time-picker'",
          $hasDateError ? "'date-error date-error'" : '',
          "'one-way-toggle age'",
          "'. button'",
          $hasMoreOptionsToggle ? "'. options-toggle'" : '',
        ]
      : [
          "'departure date-picker'",
          $hasLocationError || $hasDateError
            ? "'location-error date-error'"
            : '',
          "'time-picker button'",
          "'one-way-toggle age'",
          $hasMoreOptionsToggle ? "'. options-toggle'" : '',
        ],
  );

export const getLargeGridTemplateAreas = ({
  $hasDestinationInput,
  $hasMoreOptionsToggle,
  $hasLocationError,
  $hasDateError,
}: FormProps) =>
  joinAreas([
    `'departure ${$hasDestinationInput ? 'destination' : 'departure'} date-picker time-picker'`,
    $hasLocationError || $hasDateError
      ? "'location-error location-error date-error date-error'"
      : '',
    "'one-way-toggle one-way-toggle age button'",
    $hasMoreOptionsToggle ? "'. . . options-toggle'" : '',
  ]);

export const getExtraLargeGridTemplateAreas = ({
  $hasDestinationInput,
  $hasMoreOptionsToggle,
  $hasLocationError,
  $hasDateError,
}: FormProps) =>
  joinAreas([
    `'departure ${$hasDestinationInput ? 'destination' : 'departure'} date-picker time-picker'`,
    $hasLocationError || $hasDateError
      ? "'location-error location-error date-error date-error'"
      : '',
    "'one-way-toggle age . button'",
    $hasMoreOptionsToggle ? "'. . . options-toggle'" : '',
  ]);

export const getThinGridTemplateAreas = ({
  $hasLocationError,
  $hasDateError,
  $hasDestinationInput,
}: Omit<FormProps, '$hasMoreOptionsToggle'>) =>
  joinAreas([
    // when destination is absent, departure spans both 1fr columns to keep column widths stable
    `'${$hasDestinationInput ? 'departure destination' : 'departure departure'} date-picker time-picker button options-toggle'`,
    $hasLocationError || $hasDateError
      ? "'location-error location-error date-error date-error . .'"
      : '',
    // second row is a single area spanning all columns — laid out with flex inside
    "'second-row second-row second-row second-row second-row second-row'",
  ]);
